import React, { useState, useMemo } from 'react';

interface FoodSourceBreakdownProps {
  micronutrientData: any;
}

interface FoodNutrientContribution {
  food_name: string;
  amount: number;
  percentage: number;
  nutrient_density: number;
}

interface NutrientSources {
  [nutrient: string]: FoodNutrientContribution[];
}

export const FoodSourceBreakdown: React.FC<FoodSourceBreakdownProps> = ({ micronutrientData }) => {
  const [selectedView, setSelectedView] = useState<'contributors' | 'foods' | 'diversity'>('contributors');
  const [selectedNutrient, setSelectedNutrient] = useState<string>('vitamin_c');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Process meal data to extract food-nutrient relationships
  const { nutrientSources, foodProfiles, diversityStats } = useMemo(() => {
    if (!micronutrientData?.meals) {
      return { nutrientSources: {}, foodProfiles: {}, diversityStats: {} };
    }

    const nutrientSources: NutrientSources = {};
    const foodProfiles: { [foodName: string]: any } = {};
    const nutrientDiversityCounts: { [nutrient: string]: Set<string> } = {};

    // Initialize nutrient tracking
    const allNutrients = [
      // Vitamins
      'vitamin_a', 'vitamin_c', 'vitamin_d', 'vitamin_e', 'vitamin_k',
      'thiamine_b1', 'riboflavin_b2', 'niacin_b3', 'pantothenic_acid_b5',
      'pyridoxine_b6', 'biotin_b7', 'folate_b9', 'vitamin_b12',
      // Minerals
      'calcium', 'iron', 'magnesium', 'zinc', 'potassium', 'sodium',
      'phosphorus', 'copper', 'selenium', 'chromium', 'molybdenum', 'iodine',
      // Amino acids
      'histidine', 'isoleucine', 'leucine', 'lysine', 'methionine',
      'phenylalanine', 'threonine', 'tryptophan', 'valine',
      // Fatty acids
      'saturated', 'monounsaturated', 'polyunsaturated', 'omega_3', 'omega_6',
      // Macros
      'protein', 'carbs', 'fat', 'fiber'
    ];

    allNutrients.forEach(nutrient => {
      nutrientSources[nutrient] = [];
      nutrientDiversityCounts[nutrient] = new Set();
    });

    // Process each meal
    micronutrientData.meals.forEach((meal: any) => {
      if (meal.foods && Array.isArray(meal.foods)) {
        meal.foods.forEach((food: any) => {
          const foodName = food.food_name || 'Unknown Food';
          const calories = parseFloat(food.calories) || 0;

          // Initialize food profile if not exists
          if (!foodProfiles[foodName]) {
            foodProfiles[foodName] = {
              name: foodName,
              total_calories: 0,
              nutrient_contributions: {},
              appearance_count: 0,
              nutrient_density_score: 0
            };
          }

          foodProfiles[foodName].total_calories += calories;
          foodProfiles[foodName].appearance_count += 1;

          // Process macros from meal.totals level
          if (meal.totals) {
            const macros = ['protein', 'carbs', 'fat', 'fiber'];
            macros.forEach(macro => {
              const value = parseFloat(meal.totals[macro]) || 0;
              if (value > 0) {
                nutrientSources[macro].push({
                  food_name: foodName,
                  amount: value,
                  percentage: 0, // Will calculate later
                  nutrient_density: calories > 0 ? value / calories * 100 : 0
                });
                
                if (!foodProfiles[foodName].nutrient_contributions[macro]) {
                  foodProfiles[foodName].nutrient_contributions[macro] = 0;
                }
                foodProfiles[foodName].nutrient_contributions[macro] += value;
                nutrientDiversityCounts[macro].add(foodName);
              }
            });
          }

          // Process micronutrients from food.nutrition
          if (food.nutrition) {
            const categories = ['vitamins', 'minerals', 'amino_acids', 'fatty_acids'];
            
            categories.forEach(category => {
              if (food.nutrition[category]) {
                Object.entries(food.nutrition[category]).forEach(([nutrient, value]: [string, any]) => {
                  const numValue = parseFloat(value) || 0;
                  if (numValue > 0 && nutrientSources[nutrient]) {
                    nutrientSources[nutrient].push({
                      food_name: foodName,
                      amount: numValue,
                      percentage: 0, // Will calculate later
                      nutrient_density: calories > 0 ? numValue / calories * 100 : 0
                    });

                    if (!foodProfiles[foodName].nutrient_contributions[nutrient]) {
                      foodProfiles[foodName].nutrient_contributions[nutrient] = 0;
                    }
                    foodProfiles[foodName].nutrient_contributions[nutrient] += numValue;
                    nutrientDiversityCounts[nutrient].add(foodName);
                  }
                });
              }
            });
          }
        });
      }
    });

    // Calculate percentages and aggregate by food name
    Object.keys(nutrientSources).forEach(nutrient => {
      // Aggregate by food name
      const foodTotals: { [foodName: string]: number } = {};
      nutrientSources[nutrient].forEach(entry => {
        if (!foodTotals[entry.food_name]) {
          foodTotals[entry.food_name] = 0;
        }
        foodTotals[entry.food_name] += entry.amount;
      });

      // Calculate total for percentage calculation
      const totalAmount = Object.values(foodTotals).reduce((sum, amount) => sum + amount, 0);

      // Create aggregated entries with percentages
      nutrientSources[nutrient] = Object.entries(foodTotals)
        .map(([foodName, amount]) => ({
          food_name: foodName,
          amount: amount,
          percentage: totalAmount > 0 ? (amount / totalAmount) * 100 : 0,
          nutrient_density: foodProfiles[foodName] && foodProfiles[foodName].total_calories > 0 
            ? (amount / foodProfiles[foodName].total_calories) * 100 
            : 0
        }))
        .sort((a, b) => b.percentage - a.percentage)
        .slice(0, 10); // Top 10 contributors
    });

    // Calculate nutrient density scores for foods
    Object.values(foodProfiles).forEach((food: any) => {
      const contributedNutrients = Object.keys(food.nutrient_contributions).length;
      food.nutrient_density_score = contributedNutrients;
    });

    // Calculate diversity statistics
    const diversityStats = Object.entries(nutrientDiversityCounts).reduce((stats, [nutrient, foodSet]) => {
      stats[nutrient] = {
        unique_sources: foodSet.size,
        diversity_score: foodSet.size >= 5 ? 'High' : foodSet.size >= 3 ? 'Medium' : 'Low'
      };
      return stats;
    }, {} as any);

    return { nutrientSources, foodProfiles, diversityStats };
  }, [micronutrientData]);

  // Filter foods based on search
  const filteredFoods = useMemo(() => {
    return Object.values(foodProfiles).filter((food: any) => 
      food.name.toLowerCase().includes(searchTerm.toLowerCase())
    ).sort((a: any, b: any) => b.nutrient_density_score - a.nutrient_density_score);
  }, [foodProfiles, searchTerm]);

  // Get nutrient display name
  const getNutrientDisplayName = (nutrient: string) => {
    const names: { [key: string]: string } = {
      'vitamin_a': 'Vitamin A', 'vitamin_c': 'Vitamin C', 'vitamin_d': 'Vitamin D',
      'vitamin_e': 'Vitamin E', 'vitamin_k': 'Vitamin K', 'thiamine_b1': 'Vitamin B1',
      'riboflavin_b2': 'Vitamin B2', 'niacin_b3': 'Vitamin B3', 'pantothenic_acid_b5': 'Vitamin B5',
      'pyridoxine_b6': 'Vitamin B6', 'biotin_b7': 'Vitamin B7', 'folate_b9': 'Folate',
      'vitamin_b12': 'Vitamin B12', 'calcium': 'Calcium', 'iron': 'Iron',
      'magnesium': 'Magnesium', 'zinc': 'Zinc', 'potassium': 'Potassium',
      'sodium': 'Sodium', 'phosphorus': 'Phosphorus', 'copper': 'Copper',
      'selenium': 'Selenium', 'chromium': 'Chromium', 'molybdenum': 'Molybdenum',
      'iodine': 'Iodine', 'protein': 'Protein', 'carbs': 'Carbohydrates',
      'fat': 'Fat', 'fiber': 'Fiber', 'omega_3': 'Omega-3', 'omega_6': 'Omega-6'
    };
    return names[nutrient] || nutrient.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get diversity color
  const getDiversityColor = (score: string) => {
    switch (score) {
      case 'High': return 'text-green-600 bg-green-50';
      case 'Medium': return 'text-yellow-600 bg-yellow-50';
      case 'Low': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (!micronutrientData || !micronutrientData.meals) {
    return (
      <div className="card">
        <div className="card-body">
          <p className="text-gray-500">No meal data available for food source analysis</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">🍎 Food Source Breakdown</h3>
            <p className="text-sm text-gray-500 mt-1">
              Analyze which foods contribute which nutrients to your diet
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={selectedView}
              onChange={(e) => setSelectedView(e.target.value as any)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1"
            >
              <option value="contributors">Top Contributors</option>
              <option value="foods">Food Profiles</option>
              <option value="diversity">Diversity Analysis</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card-body space-y-6">
        {selectedView === 'contributors' && (
          <>
            {/* Nutrient Selector */}
            <div className="flex flex-wrap gap-2">
              {Object.keys(nutrientSources).filter(n => nutrientSources[n].length > 0).slice(0, 12).map(nutrient => (
                <button
                  key={nutrient}
                  onClick={() => setSelectedNutrient(nutrient)}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    selectedNutrient === nutrient
                      ? 'bg-blue-100 text-blue-700 border border-blue-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {getNutrientDisplayName(nutrient)}
                </button>
              ))}
            </div>

            {/* Top Contributors for Selected Nutrient */}
            <div>
              <h4 className="font-medium text-gray-900 mb-4">
                Top Contributors for {getNutrientDisplayName(selectedNutrient)}
              </h4>
              <div className="space-y-3">
                {nutrientSources[selectedNutrient]?.slice(0, 8).map((source, index) => (
                  <div key={`${source.food_name}-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <span className="text-lg font-semibold text-blue-600 w-6">#{index + 1}</span>
                      <div>
                        <div className="font-medium text-gray-900">{source.food_name}</div>
                        <div className="text-sm text-gray-500">
                          Density: {source.nutrient_density.toFixed(2)} per 100 cal
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">
                          {source.percentage.toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-500">
                          {source.amount.toFixed(2)} total
                        </div>
                      </div>
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full"
                          style={{ width: `${Math.min(source.percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )) || (
                  <div className="text-center py-8 text-gray-500">
                    No data available for {getNutrientDisplayName(selectedNutrient)}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {selectedView === 'foods' && (
          <>
            {/* Search Bar */}
            <div className="flex items-center space-x-4">
              <input
                type="text"
                placeholder="Search foods..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
              <div className="text-sm text-gray-500">
                {filteredFoods.length} foods found
              </div>
            </div>

            {/* Food Profiles */}
            <div className="grid gap-4">
              {filteredFoods.slice(0, 10).map((food: any) => (
                <div key={food.name} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h5 className="font-medium text-gray-900">{food.name}</h5>
                      <div className="text-sm text-gray-500">
                        {food.total_calories.toFixed(0)} total calories • {food.appearance_count} times consumed
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-blue-600">
                        {food.nutrient_density_score} nutrients
                      </div>
                      <div className="text-xs text-gray-500">contributed</div>
                    </div>
                  </div>
                  
                  {/* Top Nutrients from this Food */}
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(food.nutrient_contributions)
                      .sort(([,a]: any, [,b]: any) => b - a)
                      .slice(0, 6)
                      .map(([nutrient, amount]: [string, any]) => (
                        <span
                          key={nutrient}
                          className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded"
                        >
                          {getNutrientDisplayName(nutrient)}: {amount.toFixed(1)}
                        </span>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {selectedView === 'diversity' && (
          <>
            <div>
              <h4 className="font-medium text-gray-900 mb-4">Nutrient Source Diversity</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(diversityStats)
                  .filter(([, stats]: any) => stats.unique_sources > 0)
                  .sort(([, a]: any, [, b]: any) => b.unique_sources - a.unique_sources)
                  .slice(0, 18)
                  .map(([nutrient, stats]: [string, any]) => (
                    <div key={nutrient} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 text-sm">
                          {getNutrientDisplayName(nutrient)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {stats.unique_sources} food sources
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getDiversityColor(stats.diversity_score)}`}>
                        {stats.diversity_score}
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Diversity Insights */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h5 className="font-medium text-blue-900 mb-2">💡 Diversity Insights</h5>
              <div className="text-sm text-blue-800 space-y-1">
                <p>• <strong>High Diversity (5+ sources):</strong> Excellent variety, lower risk of deficiency</p>
                <p>• <strong>Medium Diversity (3-4 sources):</strong> Good variety, consider adding more sources</p>
                <p>• <strong>Low Diversity (1-2 sources):</strong> Consider diversifying to reduce dependency</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};